/**
 * Returns a new `SalesDealEstimate` where the `hours` of sections and subsections
 * are updated to reflect any user-applied `overrides`. The summary totals are also
 * recalculated to remain mathematically consistent with the new section values.
 */
export function getEffectiveEstimateData(result) {
    const { estimate, overrides = {} } = result;
    // 1. Fold overrides into sections and subsections
    const effectiveSections = estimate.sections.map((section) => {
        let sectionHours = 0;
        const effectiveSubsections = section.subsections.map((sub) => {
            // Every line is summed here. That already follows `countsTowardEstimate` (costed OR
            // overridden): non-costed scope rows are built at 0h (`buildAdhocScopeSection`), so they only
            // add hours through an override. The SOW line items apply the same rule explicitly.
            const ov = overrides[`subsection:${section.sectionKey}:${sub.subsectionKey}`];
            if (!ov) {
                sectionHours += sub.hours;
                return sub;
            }
            sectionHours += ov.overrideHours;
            // A user-pinned override supersedes the grounded estimate: the cohort range no longer
            // describes this number, so drop it (the estimate then shows a plain user-set figure).
            return { ...sub, hours: ov.overrideHours, rangeLow: undefined, rangeHigh: undefined };
        });
        // If a section has no subsections, preserve its original hours.
        if (section.subsections.length === 0) {
            sectionHours = section.hours;
        }
        return {
            ...section,
            hours: sectionHours,
            subsections: effectiveSubsections,
        };
    });
    // 2. Recompute summary totals based on the effective section hours
    let implementationHours = 0;
    let optionalModulesHours = 0;
    let pmHours = 0;
    let testingHours = 0;
    let goLiveHours = 0;
    let postGoLiveHours = 0;
    for (const section of effectiveSections) {
        switch (section.sectionKey) {
            case 'optional_modules':
                optionalModulesHours += section.hours;
                break;
            case 'project_management':
                pmHours += section.hours;
                break;
            case 'testing':
                testingHours += section.hours;
                break;
            case 'go_live':
                goLiveHours += section.hours;
                break;
            case 'post_go_live':
                postGoLiveHours += section.hours;
                break;
            default:
                // Any section not explicitly carved out rolls up into core implementation hours.
                implementationHours += section.hours;
                break;
        }
    }
    const grandTotalHours = effectiveSections.reduce((sum, s) => sum + s.hours, 0);
    // 3. Return the structurally identical clone with updated hours
    return {
        ...estimate,
        summary: {
            implementationHours,
            optionalModulesHours,
            pmHours,
            testingHours,
            goLiveHours,
            postGoLiveHours,
            grandTotalHours,
        },
        sections: effectiveSections,
    };
}
//# sourceMappingURL=estimate-utils.js.map